import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@shared/api";
import { Banner, Empty, Field, Loading } from "@shared/ui";
import {
  LOCALE_OPTIONS,
  PLATFORM_LABELS,
  PLATFORM_OPTIONS,
  TIMEZONE_OPTIONS,
  providerKeyFor,
  type Channel,
  type Member,
  type ProviderInfo,
  type Workspace,
} from "@shared/types";

interface DetailResponse {
  workspace: Workspace;
  members: Member[];
  channels: Channel[];
}

export function WorkspaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setData(await api.get<DetailResponse>(`/api/admin/workspaces/${id}`));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this client");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Banner kind="error">{error}</Banner>;
  if (!data) return <Loading label="Loading client" />;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/workspaces" className="muted" style={{ textDecoration: "none" }}>
            ← Clients
          </Link>
          <h1 style={{ marginTop: 6 }}>{data.workspace.name}</h1>
          <p className="page-sub">
            {data.workspace.timezone} · default language {data.workspace.defaultLocale}
          </p>
        </div>
      </div>

      <BrandCard workspace={data.workspace} onSaved={load} />
      <MembersCard workspaceId={data.workspace.id} members={data.members} onChanged={load} />
      <ChannelsCard workspaceId={data.workspace.id} channels={data.channels} onChanged={load} />
    </>
  );
}

function BrandCard({ workspace, onSaved }: { workspace: Workspace; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: workspace.name,
    timezone: workspace.timezone,
    defaultLocale: workspace.defaultLocale,
    brandVoice: workspace.brandVoice ?? "",
    brandNotes: workspace.brandNotes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.patch(`/api/admin/workspaces/${workspace.id}`, {
        ...form,
        brandVoice: form.brandVoice || null,
        brandNotes: form.brandNotes || null,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 style={{ marginBottom: 14 }}>Brand and settings</h2>
      {error ? <Banner kind="error">{error}</Banner> : null}
      {saved ? <Banner kind="ok">Saved.</Banner> : null}

      <Field label="Client name">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <div className="row">
        <Field label="Timezone">
          <select
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz}>{tz}</option>
            ))}
          </select>
        </Field>
        <Field label="Default language">
          <select
            value={form.defaultLocale}
            onChange={(e) => setForm({ ...form, defaultLocale: e.target.value })}
          >
            {LOCALE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        label="Brand voice"
        hint="How this client sounds. Goes into every prompt, so be specific about tone, words to avoid, and what they never say."
      >
        <textarea
          value={form.brandVoice}
          onChange={(e) => setForm({ ...form, brandVoice: e.target.value })}
          placeholder="Direct and practical. Speaks to operations managers, not executives. Never uses the word 'solution'."
        />
      </Field>
      <Field
        label="Background"
        hint="Products, audience, links, anything the AI needs to write credibly without inventing facts."
      >
        <textarea
          value={form.brandNotes}
          onChange={(e) => setForm({ ...form, brandNotes: e.target.value })}
          style={{ minHeight: 120 }}
        />
      </Field>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function MembersCard({
  workspaceId,
  members,
  onChanged,
}: {
  workspaceId: string;
  members: Member[];
  onChanged: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"approver" | "viewer">("approver");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/workspaces/${workspaceId}/members`, {
        email,
        name: name || null,
        role,
      });
      setEmail("");
      setName("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add this person");
    } finally {
      setBusy(false);
    }
  }

  async function remove(memberId: string) {
    await api.delete(`/api/admin/workspaces/${workspaceId}/members/${memberId}`);
    onChanged();
  }

  return (
    <div className="card">
      <h2>People who review</h2>
      <p className="muted" style={{ marginTop: 4, marginBottom: 14 }}>
        Only these email addresses can open this client's review link. Approvers can accept or
        decline posts; viewers can only comment.
      </p>

      {error ? <Banner kind="error">{error}</Banner> : null}

      {members.length === 0 ? (
        <Empty title="Nobody added yet">
          Add the client contact who will approve the weekly plan.
        </Empty>
      ) : (
        <ul className="list">
          {members.map((m) => (
            <li key={m.id} className="list-item">
              <div>
                <div style={{ fontWeight: 500 }}>{m.name || m.email}</div>
                {m.name ? <div className="muted">{m.email}</div> : null}
              </div>
              <div className="actions">
                <span className="pill">{m.role}</span>
                <button className="small danger" onClick={() => void remove(m.id)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={add} style={{ marginTop: 16 }}>
        <div className="row">
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="anna@client.com"
            />
          </Field>
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value as "approver" | "viewer")}>
              <option value="approver">Approver</option>
              <option value="viewer">Viewer</option>
            </select>
          </Field>
        </div>
        <div className="actions">
          <button type="submit" disabled={busy || !email.trim()}>
            {busy ? "Adding…" : "Add person"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChannelsCard({
  workspaceId,
  channels,
  onChanged,
}: {
  workspaceId: string;
  channels: Channel[];
  onChanged: () => void;
}) {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [manualPlatform, setManualPlatform] = useState("linkedin");
  const [manualName, setManualName] = useState("");
  const [linkPlatform, setLinkPlatform] = useState("x");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ providers: ProviderInfo[] }>("/api/admin/queue/providers")
      .then((d) => setProviders(d.providers))
      .catch(() => setProviders([]));
  }, []);

  const configured = new Set(providers.filter((p) => p.configured).map((p) => p.key));

  async function addManual(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/admin/workspaces/${workspaceId}/channels`, {
        platform: manualPlatform,
        displayName: manualName,
      });
      setManualName("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the account");
    } finally {
      setBusy(false);
    }
  }

  async function makeLink() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res = await api.post<{ url: string }>("/api/admin/queue/connect-link", {
        workspaceId,
        platform: linkPlatform,
      });
      setLink(res.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create a link");
    } finally {
      setBusy(false);
    }
  }

  async function remove(channelId: string) {
    await api.delete(`/api/admin/workspaces/${workspaceId}/channels/${channelId}`);
    onChanged();
  }

  return (
    <div className="card">
      <h2>Connected accounts</h2>
      <p className="muted" style={{ marginTop: 4, marginBottom: 14 }}>
        Where this client's posts get published.
      </p>

      {error ? <Banner kind="error">{error}</Banner> : null}

      {channels.length === 0 ? (
        <Empty title="No accounts yet">
          Connect one below, or add a placeholder so you can draft content before the accounts are
          hooked up.
        </Empty>
      ) : (
        <ul className="list">
          {channels.map((ch) => (
            <li key={ch.id} className="list-item">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{ch.displayName}</div>
                <div className="muted">
                  {PLATFORM_LABELS[ch.platform] ?? ch.platform}
                  {ch.handle ? ` · @${ch.handle}` : ""}
                </div>
                {ch.lastError ? (
                  <div className="mono" style={{ color: "var(--danger)", marginTop: 4 }}>
                    {ch.lastError}
                  </div>
                ) : null}
              </div>
              <div className="actions">
                <span className={`pill ${ch.status === "connected" ? "ok" : "warn"}`}>
                  {ch.status.replace(/_/g, " ")}
                </span>
                {configured.has(providerKeyFor(ch.platform)) ? (
                  <a
                    className="pill"
                    href={`/oauth/${providerKeyFor(ch.platform)}/start?ws=${workspaceId}`}
                  >
                    {ch.status === "connected" ? "Reconnect" : "Connect"}
                  </a>
                ) : null}
                <button className="small danger" onClick={() => void remove(ch.id)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ marginTop: 22 }}>Connect an account</h3>
      <p className="muted" style={{ marginTop: 4, marginBottom: 10 }}>
        Sign in yourself for accounts your business already administers. For everything else, send
        the client a link they open on their own phone.
      </p>

      <div className="actions" style={{ marginBottom: 14 }}>
        {providers.map((p) => (
          <a
            key={p.key}
            className={p.configured ? "pill ok" : "pill"}
            href={p.configured ? `/oauth/${p.key}/start?ws=${workspaceId}` : undefined}
            style={p.configured ? undefined : { opacity: 0.55, cursor: "not-allowed" }}
            title={p.configured ? undefined : "Credentials for this platform are not set up yet"}
          >
            {p.configured ? `Connect ${p.label}` : `${p.label} not set up`}
          </a>
        ))}
      </div>

      <div className="row" style={{ alignItems: "flex-end" }}>
        <Field label="Send the client a connect link">
          <select value={linkPlatform} onChange={(e) => setLinkPlatform(e.target.value)}>
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABELS[p] ?? p}
              </option>
            ))}
          </select>
        </Field>
        <div className="field">
          <button type="button" onClick={() => void makeLink()} disabled={busy}>
            Create link
          </button>
        </div>
      </div>

      {link ? (
        <div style={{ marginBottom: 14 }}>
          <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          <div className="actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="small"
              onClick={() => {
                void navigator.clipboard?.writeText(link);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            <span className="muted">
              Works once, expires in three days. Send it to the client however you normally talk.
            </span>
          </div>
        </div>
      ) : null}

      <h3 style={{ marginTop: 22 }}>Add a placeholder</h3>
      <p className="muted" style={{ marginTop: 4, marginBottom: 10 }}>
        Lets you draft and review content before the real account is connected. It cannot publish.
      </p>
      <form onSubmit={addManual}>
        <div className="row">
          <Field label="Platform">
            <select value={manualPlatform} onChange={(e) => setManualPlatform(e.target.value)}>
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {PLATFORM_LABELS[p] ?? p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Account name">
            <input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="Acme on LinkedIn"
              required
            />
          </Field>
        </div>
        <div className="actions">
          <button type="submit" disabled={busy || !manualName.trim()}>
            Add placeholder
          </button>
        </div>
      </form>
    </div>
  );
}
